import { Box, Button, Grid, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  OrganizationVaProfile,
  VaNicheSpecificDetails,
} from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";

import { Template } from "features/Influencer/ContactList/schema";
import { makeDeepCopy } from "utils/updateLocalState";

import BackgroundForm from "./CopywritersFlow/Background";
import FinalTouchForm from "./CopywritersFlow/FinalTouch";
import TemplateNicheQuestion from "./CopywritersFlow/TemplateNicheQuestion";
import DefaultTemplateSetUp from "./DefaultTemplateSetUp";
import { TemplateToWrite } from "./schema";

const BoughtTemplatesSteps = [
  "Build Your Template",
  "Your Background",
  "Final Touch",
];

interface Props {
  organizationVaProfile: OrganizationVaProfile | null;
  numOfTemplates: number;
  saveOrgVaProfile: (
    organizationVaProfile: OrganizationVaProfile,
    excludeIncludeFiltersChange?: boolean,
    moveNext?: boolean,
  ) => void;
  saveOrgProfile: (onboardingForm: Map, moveNext?: boolean) => void;
  setOrganizationVaProfile: (
    value: SetStateAction<OrganizationVaProfile | null>,
  ) => void;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  loading: boolean;
}

const TemplatesPaidPick = ({
  organizationVaProfile,
  setOrganizationVaProfile,
  numOfTemplates,
  saveOrgVaProfile,
  onboardingForm,
  setOnboardingForm,
  saveOrgProfile,
  loading,
}: Props) => {
  const { setAlert } = useContext(AlertContext);
  const slideContext = useContext(SlideAnimationContext);
  const { outreachTemplates, followupTemplates } = useContext(
    OutreachTemplatesContext,
  );
  const [templatesToWrite, setTemplatesToWrite] = useState<TemplateToWrite[]>(
    [],
  );
  const [defaultOutreach, setDefaultOutreach] = useState<Template | undefined>(
    undefined,
  );
  const [defaultFollowUps, setDefaultFollowUps] = useState<Template[]>([]);
  const boughtTemplates = numOfTemplates > 0;
  const needsToCreateTemplate = !boughtTemplates && !defaultOutreach;
  const [boughtTemplatesStep, setBoughtTemplatesStep] = useState<number>(0);
  const [pitchEmailValidationVa, setPitchEmailValidationVa] = useState<
    string | undefined
  >(undefined);
  const [dreamBrandValidation, setDreamBrandValidation] = useState<
    string | undefined
  >(undefined);

  const handleBack = async () => {
    if (slideContext) {
      await slideContext.slideOutDown();
      slideContext.slideInDown();
    }
    setBoughtTemplatesStep((prev) => prev - 1);
  };

  const handleSubmit = async (e: CustomEvent) => {
    e.preventDefault();
    if (!boughtTemplates) {
      handleSubmitDefault();
      return;
    }

    if (boughtTemplatesStep === 0) {
      handleSubmitNiche();
    } else if (boughtTemplatesStep === 1 && organizationVaProfile) {
      saveOrgVaProfile(organizationVaProfile, false, false);
    } else if (boughtTemplatesStep === 2) {
      if (
        (!(outreachTemplates?.length > 0) && !onboardingForm?.pitchEmail) ||
        onboardingForm?.pitchEmail === "<p></p>"
      ) {
        setPitchEmailValidationVa("Please fill out this field");
        return;
      }

      if (!(onboardingForm?.wishlistBrands?.length > 0)) {
        setDreamBrandValidation("Please fill out this field");
        return;
      }

      if (!(onboardingForm?.wishlistBrands?.length <= 10)) {
        setDreamBrandValidation("You can only set up a maximum of 10 brands");
        return;
      }
      saveOrgProfile(onboardingForm);
    }
    if (boughtTemplates && boughtTemplatesStep < BoughtTemplatesSteps.length) {
      setBoughtTemplatesStep((prev) => prev + 1);
    }
  };

  const handleSubmitNiche = () => {
    const copy = makeDeepCopy(organizationVaProfile || {});

    if (!copy?.niches) {
      setAlert("Need to pick at least 2 niches", "warning");
      return;
    }

    for (const template of templatesToWrite) {
      const replaceNicheIndex = copy?.niches?.findIndex(
        (x: VaNicheSpecificDetails) =>
          x.bento_category_id === template.niche?.bento_category_id,
      );
      if (replaceNicheIndex !== undefined) {
        copy.niches[replaceNicheIndex] = template.niche;
        copy.niches[replaceNicheIndex].needs_template = true;
      }
    }
    saveOrgVaProfile(copy, false, false);
  };

  const handleSubmitDefault = () => {
    if (!defaultOutreach) {
      setAlert("Please select a default outreach template", "warning");
      return;
    }

    const copy = makeDeepCopy(organizationVaProfile || {});
    copy.fallbackTemplateIds = [defaultOutreach.id];
    const followUpIds = defaultFollowUps.map((followUp) => followUp.id);
    if (followUpIds) {
      copy.fallbackTemplateIds.push(...followUpIds);
    }
    saveOrgVaProfile(copy);
  };

  const initializeCopywritersTemplate = () => {
    const needTemplatesNiches = organizationVaProfile?.niches?.filter(
      (niche) => niche?.needs_template,
    );
    if (needTemplatesNiches && needTemplatesNiches?.length > 0) {
      const templates: TemplateToWrite[] = needTemplatesNiches.map((niche) => ({
        niche,
      }));
      setTemplatesToWrite(templates);
      return;
    }

    const templates: TemplateToWrite[] = [];
    for (let i = 0; i < numOfTemplates; i++) {
      templates.push({ niche: undefined });
    }
    setTemplatesToWrite(templates);
  };

  const initializeDefaultTemplate = () => {
    if (outreachTemplates?.length > 0 && !defaultOutreach) {
      const defaultOutreachTemplate = outreachTemplates[0];
      setDefaultOutreach(defaultOutreachTemplate);
    }
    if (followupTemplates?.length > 0 && defaultFollowUps.length === 0) {
      const defaultFollowUpTemplate = followupTemplates[0];
      setDefaultFollowUps([defaultFollowUpTemplate]);
    }
  };

  const onChange = (e: CustomEvent, index: number) => {
    const { name, value } = e.target;
    if (name === "bento_category_id") {
      const exists = templatesToWrite.some(
        (template) => template.niche?.bento_category_id === value,
      );
      if (exists) {
        setAlert("You already picked this niche", "warning");
        return;
      }
      setTemplatesToWrite((prev) => {
        const updatedTemplates = makeDeepCopy(prev);
        updatedTemplates[index].niche = getNiche(value);
        return updatedTemplates;
      });
    } else {
      setTemplatesToWrite((prev) => {
        const updatedTemplates = makeDeepCopy(prev);
        updatedTemplates[index]["niche"] = {
          ...updatedTemplates[index]["niche"],
          [name]: value,
        };
        return updatedTemplates;
      });
    }
  };

  useEffect(() => {
    if (numOfTemplates > 0) {
      initializeCopywritersTemplate();
    } else {
      initializeDefaultTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    numOfTemplates,
    organizationVaProfile?.niches,
    outreachTemplates?.length,
    followupTemplates?.length,
  ]);

  const getNiche = (bentoCategoryId?: number) => {
    if (!bentoCategoryId || !organizationVaProfile?.niches) return undefined;
    return organizationVaProfile.niches.find(
      (niche) => niche?.bento_category_id === bentoCategoryId,
    );
  };

  const renderTemplatesStep = () => {
    if (boughtTemplatesStep === 1) {
      return (
        <BackgroundForm
          organizationVaProfile={organizationVaProfile}
          setOrganizationVaProfile={setOrganizationVaProfile}
        />
      );
    } else if (boughtTemplatesStep === 2) {
      return (
        <FinalTouchForm
          onboardingForm={onboardingForm}
          setOnboardingForm={setOnboardingForm}
          setPitchEmailValidationVa={setPitchEmailValidationVa}
          setDreamBrandValidation={setDreamBrandValidation}
          pitchEmailValidationVa={pitchEmailValidationVa}
          dreamBrandValidation={dreamBrandValidation}
        />
      );
    } else {
      return templatesToWrite?.map((template, index) => (
        <TemplateNicheQuestion
          index={index}
          nichesToPickFrom={organizationVaProfile?.niches || []}
          onChange={onChange}
          niche={template?.niche}
        />
      ));
    }
  };

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        Step 2:{" "}
        {!boughtTemplates
          ? "Select Your Pitch Template"
          : BoughtTemplatesSteps[boughtTemplatesStep]}
      </Typography>

      {!boughtTemplates && (
        <Box onClick={(e) => e.stopPropagation()}>
          <DefaultTemplateSetUp
            defaultFollowUps={defaultFollowUps}
            setDefaultFollowUps={setDefaultFollowUps}
            defaultOutreach={defaultOutreach}
            setDefaultOutreach={setDefaultOutreach}
          />
        </Box>
      )}
      <form onSubmit={handleSubmit}>
        {boughtTemplates && renderTemplatesStep()}

        <Grid item xs="auto" container columnGap={1} sx={{ mt: 4 }}>
          <Grid item xs="auto">
            {boughtTemplatesStep > 0 && (
              <Button disableElevation onClick={handleBack}>
                <Box
                  sx={{ mr: 1 }}
                  component="i"
                  className="fa-solid fa-arrow-left"
                />
                Back
              </Button>
            )}
          </Grid>
          <Grid item xs></Grid>
          <Grid item xs="auto">
            <Button
              disableElevation
              type="submit"
              disabled={needsToCreateTemplate || loading}
            >
              Next
              <Box
                sx={{ ml: 1 }}
                component="i"
                className="fa-solid fa-arrow-right"
              />
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default TemplatesPaidPick;
